// ═══════════════════════════════════════════════════════════════
// VACACIONES — Tab Políticas
// ═══════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Plus, Edit2, Trash2, Save, X, AlertCircle } from 'lucide-react';

const EMPTY_FORM = {
  id:                    null,
  name:                  '',
  years_from:            0,
  years_to:              '',
  days_per_year:         14,
  accrual_type:          'annual',
  max_carryover_days:    0,
  tipo_pago_vacaciones:  'total',
};

export default function PoliciesTab({ policies = [], loading, onSave, onDelete, onInitDefaults }) {
  const [showForm, setShowForm]   = useState(false);
  const [formData, setFormData]   = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [deleting, setDeleting]   = useState(null);
  const [formError, setFormError] = useState('');

  const openNew = () => {
    setFormData(EMPTY_FORM);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (policy) => {
    setFormData({
      id:                    policy.id,
      name:                  policy.name || '',
      years_from:            policy.years_from ?? 0,
      years_to:              policy.years_to ?? '',
      days_per_year:         policy.days_per_year ?? 14,
      accrual_type:          policy.accrual_type || 'annual',
      max_carryover_days:    policy.max_carryover_days ?? 0,
      tipo_pago_vacaciones:  policy.tipo_pago_vacaciones || 'total',
    });
    setFormError('');
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setFormError('El nombre es obligatorio.');
      return;
    }
    if (formData.days_per_year < 1) {
      setFormError('Los días por año deben ser al menos 1.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      const payload = {
        ...formData,
        years_from:        parseFloat(formData.years_from) || 0,
        years_to:          formData.years_to === '' ? null : parseFloat(formData.years_to),
        days_per_year:     parseFloat(formData.days_per_year),
        max_carryover_days: parseFloat(formData.max_carryover_days) || 0,
      };
      await onSave(payload);
      setShowForm(false);
      setFormData(EMPTY_FORM);
    } catch (err) {
      setFormError(err?.response?.data?.error || err?.message || 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta política de vacaciones?')) return;
    setDeleting(id);
    try {
      await onDelete(id);
    } finally {
      setDeleting(null);
    }
  };

  const handleInitDefaults = async () => {
    if (!window.confirm('¿Crear las políticas por defecto según la Ley 16-92? Esto agregará 3 tramos de vacaciones.')) return;
    try {
      await onInitDefaults();
    } catch {
      // error manejado en el padre
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-gray-500">
          {policies.length} {policies.length === 1 ? 'política configurada' : 'políticas configuradas'}
        </p>
        <button
          onClick={openNew}
          className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
        >
          <Plus size={16} />
          Agregar política
        </button>
      </div>

      {/* Tabla */}
      {policies.length === 0 ? (
        <EmptyState onInitDefaults={handleInitDefaults} />
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">Nombre</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">Años Desde</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">Años Hasta</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">Días/Año</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-700">Acumulación</th>
                  <th className="text-center px-4 py-3 font-semibold text-gray-700">Pago en Nómina</th>
                  <th className="text-right px-4 py-3 font-semibold text-gray-700">Arrastre Máx.</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {policies.map(policy => (
                  <tr key={policy.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-800">{policy.name}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{policy.years_from}</td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {policy.years_to != null ? policy.years_to : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-800 font-semibold">{policy.days_per_year}</td>
                    <td className="px-4 py-3 text-center">
                      <AccrualBadge type={policy.accrual_type} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <PagoBadge tipo={policy.tipo_pago_vacaciones || 'total'} />
                    </td>
                    <td className="px-4 py-3 text-right text-gray-600">
                      {policy.max_carryover_days ?? 0}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openEdit(policy)}
                          className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                          title="Editar"
                        >
                          <Edit2 size={15} />
                        </button>
                        <button
                          onClick={() => handleDelete(policy.id)}
                          disabled={deleting === policy.id}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                          title="Eliminar"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Formulario inline debajo de la tabla */}
          {showForm && (
            <InlineForm
              formData={formData}
              onChange={handleChange}
              onSave={handleSave}
              onCancel={() => { setShowForm(false); setFormError(''); }}
              saving={saving}
              error={formError}
              isEditing={!!formData.id}
            />
          )}
        </div>
      )}

      {/* Si hay políticas pero el formulario está abierto fuera de la tabla */}
      {policies.length > 0 && !showForm && (
        <div className="text-xs text-gray-400 text-center py-1">
          Las políticas se aplican al calcular días de vacaciones según la antigüedad del empleado.
        </div>
      )}

      {/* Formulario flotante cuando no hay políticas */}
      {policies.length === 0 && showForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <InlineForm
            formData={formData}
            onChange={handleChange}
            onSave={handleSave}
            onCancel={() => { setShowForm(false); setFormError(''); }}
            saving={saving}
            error={formError}
            isEditing={false}
          />
        </div>
      )}
    </div>
  );
}

// ── Formulario inline ───────────────────────────────────────────
function InlineForm({ formData, onChange, onSave, onCancel, saving, error, isEditing }) {
  return (
    <div className="border-t border-gray-200 bg-gray-50 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-4">
        {isEditing ? 'Editar política' : 'Nueva política de vacaciones'}
      </h3>

      {error && (
        <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Nombre */}
        <div className="lg:col-span-3">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Nombre <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={onChange}
            placeholder="Ej. Primer tramo (1–3 años)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        {/* Años desde */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Años Desde</label>
          <input
            type="number"
            name="years_from"
            value={formData.years_from}
            onChange={onChange}
            min={0}
            step={0.5}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        {/* Años hasta */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Años Hasta <span className="text-gray-400">(vacío = sin límite)</span>
          </label>
          <input
            type="number"
            name="years_to"
            value={formData.years_to}
            onChange={onChange}
            min={0}
            step={0.5}
            placeholder="Sin límite"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        {/* Días/Año */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Días por Año <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="days_per_year"
            value={formData.days_per_year}
            onChange={onChange}
            min={1}
            step={0.5}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>

        {/* Tipo acumulación */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de Acumulación</label>
          <select
            name="accrual_type"
            value={formData.accrual_type}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
          >
            <option value="annual">Anual</option>
            <option value="monthly">Mensual</option>
          </select>
        </div>

        {/* Tipo pago en nómina */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Pago en Nómina</label>
          <select
            name="tipo_pago_vacaciones"
            value={formData.tipo_pago_vacaciones}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
          >
            <option value="total">Pago total (salario / 23.83 × días)</option>
            <option value="prima">Prima vacacional (diferencial sobre salario regular)</option>
          </select>
          <p className="text-[11px] text-gray-400 mt-1">
            {formData.tipo_pago_vacaciones === 'prima'
              ? 'Solo se paga el extra: (sal/23.83 − sal/30) × días'
              : 'Se paga el monto completo: sal/23.83 × días'}
          </p>
        </div>

        {/* Arrastre máximo */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Arrastre Máximo (días)
          </label>
          <input
            type="number"
            name="max_carryover_days"
            value={formData.max_carryover_days}
            onChange={onChange}
            min={0}
            step={1}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
      </div>

      {/* Acciones */}
      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={onSave}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50 transition-colors"
        >
          <Save size={15} />
          {saving ? 'Guardando…' : 'Guardar'}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          <X size={15} />
          Cancelar
        </button>
      </div>
    </div>
  );
}

// ── Estado vacío ────────────────────────────────────────────────
function EmptyState({ onInitDefaults }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
      <div className="w-14 h-14 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
        <AlertCircle size={26} className="text-teal-500" />
      </div>
      <p className="text-gray-700 font-medium mb-1">No hay políticas configuradas</p>
      <p className="text-sm text-gray-500 mb-5">
        Define los tramos de vacaciones según la antigüedad, o crea las políticas por defecto de la Ley 16-92.
      </p>
      <button
        onClick={onInitDefaults}
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
      >
        <Plus size={16} />
        Crear políticas por defecto (Ley 16-92)
      </button>
    </div>
  );
}

// ── Badge tipo pago ──────────────────────────────────────────────
function PagoBadge({ tipo }) {
  if (tipo === 'prima') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
        Prima
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700">
      Total
    </span>
  );
}

// ── Badge de tipo de acumulación ────────────────────────────────
function AccrualBadge({ type }) {
  if (type === 'monthly') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        Mensual
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
      Anual
    </span>
  );
}
